import { useContext, useState } from "react";
import OutlineButton from "../Buttons/OutlineButton";
import PrimaryButton from "../Buttons/PrimaryButton";
import { PROGRESS_VERIFICATION_REPORT_STATUS } from "@constants/verificationReport";
import { useMutationData } from "@hooks/useMutationData";
import queryKeys from "@configs/react-query/query-key";
import ToastConstant from "@configs/react-toastify/snackbar";
import { TOAST } from "@constants";
import { removeSpaces, getAppLanguage } from "@utils/common";
import { extractErrorCode } from "@utils/error";
import dayjs from "dayjs";
import ExportSbomReportModal from "@components/UI/ExportSbomReportModal";
import SbomItemsListVerificationModal from "@components/UI/SbomItemsListVerificationModal";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import { VexImportFromPreviousVersionModal } from "@components/UI";
import { useClientConfig } from "@contexts/ClientConfigContext";
import { PermissionContext } from "@contexts/PermissionContext";
import { buildSbomDownloadPayload, isSbomHtmlReadableExport, resolveSbomExportLanguage } from "@constants/sbom";
import { isVexEditableVerificationStatus } from "@constants/vex";
import {
  openSbomReadableViewOrToast,
  SBOM_READABLE_VIEW_SOURCE,
} from "@utils/sbomReadableView";

// Helper component for creating headers with edit functionality
export const ComponentTableHeader = ({
  title,
  verificationReportStatus,
  count,
  isEditMode,
  isEditingAllowed,
  onEdit,
  onCancel,
  onSave,
  colors,
  t,
  verificationReportSeq,
  fetchedVerificationData,
  // SBOM Items List Modal props
  sbomMissings,
  sbomNotApplicables,
  sbomScanComponents,
  /** When set — enables VEX import action (chip lives on ComponentTable) */
  vexContext = null,
  /** Frozen snapshot — chip reads overall `progress` from GET /vex/{source} */
  frozenVexSnapshot = null,
  showVexProgress = false,
}) => {
  const { i18n } = useTranslation();
  const { colors: themeColors } = useTheme();
  const clientConfig = useClientConfig();
  const { canEditVex } = useContext(PermissionContext) || {};
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSbomItemsListModalOpen, setIsSbomItemsListModalOpen] = useState(false);
  const [isVexImportModalOpen, setIsVexImportModalOpen] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);

  const isVexOn = Boolean(clientConfig?.isClientConfigEnabled?.("useVex"));
  const canEditLiveVex = isVexEditableVerificationStatus(
    vexContext?.verificationStatusCd,
    vexContext?.scanToolCd,
  );
  const canShowVexImport =
    showVexProgress &&
    isVexOn &&
    canEditLiveVex &&
    Boolean(canEditVex?.(vexContext?.projectManagerId)) &&
    vexContext?.scanToolCd &&
    vexContext?.verificationSeq != null &&
    vexContext?.projectSeq != null;

  const downloadVerificationReportSbom = useMutationData(
    (data) => {
      const typeParam = data.sbomType ? `&sbomType=${encodeURIComponent(data.sbomType)}` : "";
      const baseUrl = `/${queryKeys.downloadVerificationReportSbom}?verificationReportSeq=${data.verificationReportSeq}${typeParam}`;
      const formatParam = data.format ? `&format=${encodeURIComponent(data.format)}` : "";
      const languageParam = `&language=${encodeURIComponent(resolveSbomExportLanguage(data.format, getAppLanguage()))}`;
      return data.includeVex
          ? `${baseUrl}&includeVex=Y${formatParam}${languageParam}`
          : `${baseUrl}&includeVex=N${formatParam}${languageParam}`;
    },
    { method: "GET", responseType: "arraybuffer" },
    {
      onSuccess: (data, variables) => {
        // Generate download file name
        const currentTimestamp = dayjs().format("YYYYMMDD");
        const reportName = fetchedVerificationData?.verificationReportName
          ? removeSpaces(fetchedVerificationData.verificationReportName)
          : "verification_report";
        const sbomName = removeSpaces(variables.sbomName);
        const verificationToolCd = fetchedVerificationData?.verificationToolCd || "";
        const { blob, extension } = buildSbomDownloadPayload(data, variables.format);

        let downloadFileName;
        if (variables.format === "CSAF") {
          downloadFileName = `${reportName}_${verificationToolCd}_CSAF_${currentTimestamp}.${extension}`;
        } else {
          downloadFileName = `${reportName}_${verificationToolCd}_${sbomName}_${currentTimestamp}.${extension}`;
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = downloadFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        setIsDownloading(false);
      },
      onError: (error) => {
        console.error("Error during download:", error);
        const errorCodeKey = extractErrorCode(error, "errorcode.00001");
        const errorMessage = errorCodeKey ? t(errorCodeKey) : (error.response?.data?.message || error.response?.data?.additionalMessage || t("toast.download.error.message"));
        ToastConstant(TOAST.error, t("toast.download.error.title"), errorMessage);
        setIsDownloading(false);
      },
    },
  );

  const handleExportSbomReport = async (selection) => {
    const { type, format, includeVex } = selection || {};
    if (!type || !verificationReportSeq) return;

    setIsDownloading(true);
    setShowExportModal(false);

    if (isSbomHtmlReadableExport(type)) {
      try {
        openSbomReadableViewOrToast(
          {
            sourceType: SBOM_READABLE_VIEW_SOURCE.VERIFICATION,
            params: {
              verificationReportSeq,
              includeVex: includeVex ? "Y" : "N",
            },
            title: fetchedVerificationData?.verificationReportName || "",
          },
          "toast.download.error.title",
        );
      } finally {
        setIsDownloading(false);
      }
      return;
    }

    handleDownloadFormatSelectWithVex(type, includeVex, format);
  };

  const handleDownloadFormatSelectWithVex = (sbomType, includeVex = false, format = "JSON") => {
    if (!sbomType || !verificationReportSeq) {
      setIsDownloading(false);
      return;
    }

    if (format === "CSAF") {
      downloadVerificationReportSbom.mutate({
        verificationReportSeq,
        sbomName: "CSAF",
        includeVex: true,
        isVexPreferenceOn: isVexOn,
        format,
      });
      return;
    }

    const targetSbomName = sbomType.sbomName || sbomType.label;
    downloadVerificationReportSbom.mutate({
      verificationReportSeq: verificationReportSeq,
      sbomType: sbomType.value,
      sbomName: targetSbomName,
      includeVex,
      isVexPreferenceOn: isVexOn,
      format,
    });
  };

  return (
  <div className="flex justify-between items-center mb-4" data-testid="component-table-header">
    <div className="flex items-center gap-3 flex-wrap">
      <h2
        className={`${colors?.textPrimary || themeColors.textPrimary} text-lg font-semibold ${
          i18n.language === "en" ? "uppercase" : ""
        }`}
      >
        {title} {count !== undefined && `(${count})`}
      </h2>
    </div>

    <div className="flex space-x-2">
      {isEditMode ? (
        <>
          <OutlineButton onClick={onCancel} size="md" className="min-w-[110px] min-h-10 justify-center">
            {t("common.cancel-button")}
          </OutlineButton>
          <PrimaryButton onClick={onSave} size="md" className="min-w-[110px] min-h-10 justify-center">
            {t("common.save-button")}
          </PrimaryButton>
        </>
      ) : isEditingAllowed ? (
        <OutlineButton onClick={onEdit} size="md">
          {t("common.edit-button")}
        </OutlineButton>
      ) : null}

      {verificationReportStatus === PROGRESS_VERIFICATION_REPORT_STATUS.DISTRIBUTION_DONE && verificationReportSeq && (
        <PrimaryButton
          onClick={() => setShowExportModal(true)}
          disabled={isDownloading}
          size="md"
        >
          {isDownloading
            ? t("project.verification-report.exporting")
            : t("project.verification-report.export-sbom-report")}
        </PrimaryButton>
      )}

      {/* SBOM Item List Button */}
        {sbomMissings && sbomNotApplicables &&
          (
            <OutlineButton
              onClick={() => setIsSbomItemsListModalOpen(true)}
              size="md"
            >
              {t("project.sbom.item-list-title")}
            </OutlineButton>
          )}

      {canShowVexImport && (
        <OutlineButton
          onClick={() => setIsVexImportModalOpen(true)}
          size="md"
          data-testid="component-table-vex-import-button"
        >
          {t("status.vex-import-from-previous-version")}
        </OutlineButton>
      )}
    </div>


    {/* SBOM Items List Modal */}
    {sbomMissings && sbomNotApplicables && (
      <SbomItemsListVerificationModal
        isOpen={isSbomItemsListModalOpen}
        onClose={() => setIsSbomItemsListModalOpen(false)}
        title={t("project.sbom.item-list-title")}
        missings={sbomMissings}
        notApplicables={sbomNotApplicables}
        itemsPerPage={5}
        firstColumnTitle={t("project.sbom.item-missing-list")}
        secondColumnTitle={t("project.sbom.item-not-applicable-list")}
        scanComponents={sbomScanComponents || []}
      />
    )}

    {canShowVexImport && (
      <VexImportFromPreviousVersionModal
        show={isVexImportModalOpen}
        onClose={() => setIsVexImportModalOpen(false)}
        projectSeq={vexContext.projectSeq}
        scanToolCd={vexContext.scanToolCd}
        targetVerificationSeq={vexContext.verificationSeq}
        projectManagerId={vexContext.projectManagerId}
      />
    )}

    <ExportSbomReportModal
      isOpen={showExportModal}
      onClose={() => setShowExportModal(false)}
      onExport={handleExportSbomReport}
      exporting={isDownloading}
    />
  </div>
  )
};

export default ComponentTableHeader;